import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { SIDEBAR, MODAL } from '~/ui/ui-constants.ts';
import UIContext from '~/ui/ui-context.ts';
import { isShowAppBannerTrue } from '~/store/cookies.js';
import { isSidebarAnimationDisabled, isNativeComponentsEnabled } from '~/components/app-bridge/app-properties.js';
import { selectHasRegulationsHeader } from '~/components/header/regulations-header/index.js';
import { selectAppBanner } from '~/components/app-banner/ducks/index.js';
import useShouldShowContexts from './use-should-show-contexts.js';
import useCreateContextRefs from './use-create-context-refs.js';
import useTransparentModalRoute from './use-transparent-modal-route.js';

import { selectIsDesktop } from '~/components/brand/ducks/selectors.js';

const CONTEXT_KEYS = [SIDEBAR, MODAL];

const UIContextProvider = ({
	contexts,
	hasHeader,
	hasSmallerBrandingHeader,
	hasTopNavigation,
	hasSubHeader,
	hasSmallSubHeader,
	hasSearchBar,
	hasProductNav,
	hasHeroHeader,
	hasFooter,
	hasSidebar,
	hasModal,
	forceAbsoluteHeader,
	forceAbsoluteSubHeader,
	hasNativeHeader,
	hasNativeBottomNavigation,
	children
}) => {
	const [showSidebar, showModal] = useShouldShowContexts(contexts, CONTEXT_KEYS);
	const contextRefs = useCreateContextRefs(contexts);
	const transparentModalRoute = useTransparentModalRoute();

	const sidebarAnimationEnabled = !isSidebarAnimationDisabled();
	const nativeComponentEnabled = isNativeComponentsEnabled();
	const appBanner = useSelector(selectAppBanner);
	const isDesktop = useSelector(selectIsDesktop);
	const hasRegulationsHeader = useSelector(selectHasRegulationsHeader);

	const appBannerEnabled = isShowAppBannerTrue() && appBanner && appBanner.enabled;

	const uiContext = useMemo(
		() => ({
			hasSidebar,
			hasModal,
			hasHeader,
			hasSmallerBrandingHeader,
			hasTopNavigation,
			hasSubHeader,
			hasSmallSubHeader,
			hasSearchBar,
			hasStaticHeader: hasRegulationsHeader,
			hasProductNav,
			hasHeroHeader,
			hasAppBanner: appBannerEnabled,
			hasFooter,
			showSidebar,
			showModal,
			transparentModal: transparentModalRoute,
			withinSidebar: false, // TODO consider removing
			within: '',
			scrollbar: undefined,
			nativeComponentEnabled,
			hasNativeHeader,
			hasNativeBottomNavigation,
			sidebarAnimationEnabled,
			contexts,
			contextRefs: contextRefs.current,
			isDesktop,
			forceAbsoluteHeader,
			forceAbsoluteSubHeader
		}),
		[
			contexts,
			hasSidebar,
			hasModal,
			nativeComponentEnabled,
			hasNativeHeader,
			hasNativeBottomNavigation,
			appBannerEnabled,
			hasRegulationsHeader,
			hasFooter,
			hasHeader,
			hasSmallerBrandingHeader,
			hasTopNavigation,
			hasSubHeader,
			hasSmallSubHeader,
			hasSearchBar,
			hasProductNav,
			hasHeroHeader,
			showSidebar,
			showModal,
			sidebarAnimationEnabled,
			transparentModalRoute,
			contextRefs,
			isDesktop,
			forceAbsoluteHeader,
			forceAbsoluteSubHeader
		]
	);

	return <UIContext.Provider value={uiContext}>{children}</UIContext.Provider>;
};

export default UIContextProvider;
